/**
 * DeletePaywallDialog — 删除支付页面弹窗（ADM-PAY-S004 · delete-paywall）
 *
 * 1:1 移植自原型 design/Novel-admin/src/dialogs3.jsx 203–268（DeletePaywallDialog）。
 * 接真：删除走 paymentPagesApi.remove(page.id)（DELETE /api/v1/payment-pages/:id）。
 * 引用校验前置（对齐原型 refs 由父级预算、blocked 开门即定）：
 *  - 打开即 useQuery 预检 GET /payment-pages/:id/references——有活跃引用直接进「无法删除」阻断视图；
 *    无引用在确认正文渲染绿色「无活跃引用」已校验状态行（预检失败则回退将来时文案，不阻塞删除）
 *  - 点删 → mutation；server 事务内校验保留兜底（防 TOCTOU）——若抛 PAY_PAGE_SCENARIO_REFERENCED
 *    仍切阻断视图，按 err.details.references 列出占用项（kind/id/label），引导运营先清理
 *  - 其他错误 → 错误 toast（不假成功）
 * 成功 → toast + invalidate ['payment-pages', appId] + onClose。
 * 原型 DialogShell → ~/ui/Dialog 的 Dialog 壳；window.toast → ~/ui/Toast 的 toast。
 */

import { ADMIN_ERROR_CODES, type PaymentPageScenarioReference } from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { type PaywallPageItem, appQueryKeys } from '~/data/queries';
import { paymentPagesApi } from '~/hooks/use-payments';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';

/** scenario 引用一项（预检响应与 PAY_PAGE_SCENARIO_REFERENCED.details.references 同形态）
 *  终态 kind（#243：迁移前 legacy push_task scenario scan 已移除——push 全程 offer_id 寻址、不再进删页阻断；
 *  banner/push 引用完整性由删 offer 校验承接 ADM-PAY-S005）= {payment_offer, operational_popup_scene} */
type ScenarioReference = PaymentPageScenarioReference;

/** server reference.kind → 运营可读中文 */
const REF_KIND_LABEL: Record<ScenarioReference['kind'], string> = {
  // #228 决议 3：banner/外部入口改引 offer_id，阻断引用收敛为生效中的运营活动 offer
  payment_offer: '运营活动 offer',
  // operational_popup 页专属：该页是运营弹窗各场景共用的统一 paywall placement（dev-docs #126 ③）
  operational_popup_scene: '运营弹窗场景',
};

/** 引用项「去清理」跳转目标：占用方资源页（运营先去清引用，再回来删页）。
 *  路由按 router/index.tsx 已注册路径——offer 在 /paywall 的「限时优惠 / Offer」tab（OfferMirror），
 *  popup scene 在 /ops 的「运营弹窗」tab；两目标页均读 ?tab= 锚点切到对应 tab（一次性消费后即清参）。 */
const REF_KIND_CLEANUP_ROUTE: Record<ScenarioReference['kind'], string> = {
  payment_offer: '/paywall?tab=offers',
  operational_popup_scene: '/ops?tab=popup',
};

function parseReferences(err: unknown): ScenarioReference[] {
  if (!(err instanceof AdminApiError)) return [];
  const refs = err.details?.references;
  if (!Array.isArray(refs)) return [];
  return refs as ScenarioReference[];
}

export function DeletePaywallDialog({
  page,
  onClose,
}: {
  page: PaywallPageItem;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');

  // 「去清理」：跳到占用方资源页让运营先清引用，跳前先关本弹窗（避免回退后悬挂在删除流程）；
  // 跳转后 info toast 引导（对齐原型）——告知落点与待清理引用，落地后不失去清理上下文
  const goCleanup = (ref: ScenarioReference) => {
    onClose();
    navigate(REF_KIND_CLEANUP_ROUTE[ref.kind]);
    toast(
      `已跳转到「${REF_KIND_LABEL[ref.kind]}」· 请清理对支付页面「${page.name}」的引用 (${ref.id})`,
      {
        tone: 'info',
        icon: 'IconExternal',
      },
    );
  };
  // 引用校验前置（对齐原型 blocked 开门即定）：打开即预检；staleTime:0 保证每次开门都拉新
  // （全局默认 30s 缓存会让「清完引用马上重开」误判仍被阻断）
  const referencesQuery = useQuery({
    queryKey: ['payment-page-references', page.id],
    queryFn: () => paymentPagesApi.listReferences(page.id),
    staleTime: 0,
  });
  const precheckRefs = referencesQuery.data?.references;
  // 阻断引用兜底：server 删除事务内校验命中 PAY_PAGE_SCENARIO_REFERENCED 后填充（防预检后并发新增引用）
  const [serverBlockedRefs, setServerBlockedRefs] = useState<ScenarioReference[] | null>(null);
  // 阻断视图数据源：删除时兜底结果优先（更新），否则用预检结果
  const blockedRefs =
    serverBlockedRefs ?? (precheckRefs && precheckRefs.length > 0 ? precheckRefs : null);

  const deleteMutation = useMutation({
    mutationFn: () => paymentPagesApi.remove(page.id),
    onSuccess: async () => {
      toast(`已删除支付页面「${page.name}」`, { tone: 'warn', icon: 'IconTrash' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['payment-pages', page.appId] }),
        queryClient.invalidateQueries({ queryKey: appQueryKeys.paymentMethods(page.appId) }),
        // 删运营弹窗页会级联软删该 App 的 operational_popup 渠道行 → 运营弹窗页只读落点区跟着失效
        ...(page.scenario === 'operational_popup'
          ? [queryClient.invalidateQueries({ queryKey: ['popup-placements', page.appId] })]
          : []),
      ]);
      onClose();
    },
    onError: (err) => {
      if (
        err instanceof AdminApiError &&
        err.code === ADMIN_ERROR_CODES.PAY_PAGE_SCENARIO_REFERENCED
      ) {
        // 切阻断视图，列出占用项（不假成功）
        setServerBlockedRefs(parseReferences(err));
        return;
      }
      toast(err instanceof Error ? err.message : '删除失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const blocked = blockedRefs !== null && blockedRefs.length > 0;

  return (
    <Dialog
      width={560}
      title={blocked ? `无法删除「${page.name}」` : `删除支付页面「${page.name}」？`}
      subtitle={
        blocked
          ? '该页面 scenario 存在活跃引用 · 请先清理引用再删除'
          : 'ADM-PAY-S004 · 仅运营新增页面 (is_built_in=false) 可删除'
      }
      icon={blocked ? 'IconBan' : 'IconTrash'}
      onClose={onClose}
      footer={
        blocked ? (
          <>
            <div style={{ flex: 1 }} />
            <button className="btn btn-accent" onClick={onClose}>
              我知道了
            </button>
          </>
        ) : (
          <>
            <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
              操作将记入审计日志
            </div>
            <button className="btn" onClick={onClose} disabled={deleteMutation.isPending}>
              取消
            </button>
            <button
              className="btn btn-danger"
              disabled={typed !== page.name || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              <Icons.IconTrash size={11} />
              {deleteMutation.isPending ? '删除中…' : '永久删除'}
            </button>
          </>
        )
      }
    >
      <div style={{ padding: 18 }}>
        {blocked ? (
          <>
            <div
              className="card-flat"
              style={{
                padding: 12,
                background: 'var(--neg-soft)',
                borderColor: 'transparent',
                marginBottom: 12,
              }}
            >
              <div className="hstack" style={{ gap: 8 }}>
                <Icons.IconAlert size={13} style={{ color: 'var(--neg)' }} />
                <span style={{ fontSize: 12.5 }}>
                  删除前校验失败 · 该页 scenario 被{' '}
                  <span className="mono strong">{blockedRefs?.length}</span> 处配置引用
                </span>
              </div>
            </div>
            <div className="vstack" style={{ gap: 6 }}>
              {blockedRefs?.map((r) => (
                <div
                  key={`${r.kind}:${r.id}`}
                  className="hstack"
                  style={{
                    gap: 10,
                    padding: '8px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                  }}
                >
                  <span className="chip chip-warn" style={{ fontSize: 10, flexShrink: 0 }}>
                    {REF_KIND_LABEL[r.kind]}
                  </span>
                  <span style={{ flex: 1, fontSize: 12 }}>{r.label}</span>
                  <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {r.id}
                  </span>
                  {/* 去清理：跳到占用方资源页让运营先解绑/清空该引用，再回来删页 */}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ flexShrink: 0 }}
                    title={`前往${REF_KIND_LABEL[r.kind]}页清理该引用`}
                    onClick={() => goCleanup(r)}
                  >
                    去清理
                    <Icons.IconArrowRight size={11} />
                  </button>
                </div>
              ))}
            </div>
            <div
              style={{ marginTop: 12, fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.6 }}
            >
              {/* offer 校验范围含生效中运营活动 offer：#228 决议 3 */}
              引用来源校验范围：<span className="mono">app_payment_methods</span>（级联软删）·{' '}
              <span className="mono">app_payment_offers</span>（生效中运营活动 offer）·{' '}
              <span className="mono">app_operational_popup_scenes</span>
              （仅运营弹窗页）。清理完成后可重新发起删除。
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12.5, lineHeight: 1.65, color: 'var(--fg)' }}>
              删除后该页面配置不可恢复。该页面的 <span className="mono">scenario</span>{' '}
              在客户端仍可用（配置解耦），请先确保无活跃引用。
              {/* 前置预检三态：已校验无引用（原型 pos 色状态行）/ 校验中 / 预检失败回退将来时文案
                  （删除时 server 事务内校验兜底，预检挂掉不阻塞删除路径） */}
              {referencesQuery.isSuccess ? (
                <>
                  已校验{' '}
                  <span className="mono">
                    app_payment_methods / app_payment_offers / app_operational_popup_scenes
                  </span>
                  ：<span style={{ color: 'var(--pos)', fontWeight: 500 }}>无活跃引用</span>。
                </>
              ) : referencesQuery.isError ? (
                <>
                  删除时将校验{' '}
                  <span className="mono">
                    app_payment_methods / app_payment_offers / app_operational_popup_scenes
                  </span>
                  ， 有活跃引用则阻止删除。
                </>
              ) : (
                <>
                  正在校验{' '}
                  <span className="mono">
                    app_payment_methods / app_payment_offers / app_operational_popup_scenes
                  </span>{' '}
                  的活跃引用…
                </>
              )}
            </div>
            <div style={{ marginTop: 14 }}>
              <div className="label" style={{ marginBottom: 6 }}>
                输入页面名称「{page.name}」以确认
              </div>
              <input
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={page.name}
                autoFocus
              />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
